import { Info } from 'lucide-react'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { AppShell } from '@/components/px/AppShell'
import { DensityNote } from '@/components/px/DensityNote'
import { KanbanBoard } from '@/components/px/KanbanBoard'
import { Panel } from '@/components/px/Panel'
import { PersonChip } from '@/components/px/PersonChip'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { myNav } from '@/components/px/nav'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { BOARD_LABELS, COLUMN_LABELS } from '@/demo/content/myBoard'
import { healthTone } from '@/demo/content/portfolioBoard'
import {
  MANAGER_CHROME,
  MANAGER_DENSITY_NOTE,
  MANAGER_HEADER,
  MANAGER_LABELS,
  benchNote,
  benchPeople,
  capacityGap,
  overloadedNote,
  overloadedPeople,
  progressRows,
  scopeLine,
  teamWorkPackages,
  type LoadedPerson,
} from '@/demo/content/managerLanding'
import {
  OFFICES,
  WORK_PACKAGE_STATUSES,
  personById,
  roleByKey,
  type OfficeCode,
} from '@/demo/data'
import { useAppSelector } from '@/store'
import { selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'

/**
 * `/my/team` — the manager landing. Three questions, in the order a manager
 * asks them: which engagements are at risk, who is over-loaded or free, and
 * what is on the whole team's board.
 *
 * The board underneath is the SAME `KanbanBoard` the consultant lands on, but
 * scoped to every package on `visibleProjects('manager')` rather than to the
 * manager's own (AC-3.3) — and with `showAssignee`, because a people surface
 * cannot be read when a card has no owner.
 */
const ANCHOR_OFFICE: OfficeCode = OFFICES[0]?.code ?? 'NL'

export default function ManagerLandingPage() {
  const role = useAppSelector(selectActiveRoleKey)
  const office = useMemo(() => {
    const personId = roleByKey(role).personId
    return personId ? personById(personId).office : ANCHOR_OFFICE
  }, [role])

  const rows = useMemo(() => progressRows(role), [role])
  const board = useMemo(() => teamWorkPackages(role), [role])
  const scope = useMemo(() => scopeLine(role, office), [role, office])
  const overloaded = useMemo(() => overloadedPeople(), [])
  const bench = useMemo(() => benchPeople(), [])
  const gap = useMemo(() => capacityGap(), [])

  return (
    <AppShell
      moduleLabel={MANAGER_CHROME.moduleLabel}
      breadcrumb={MANAGER_CHROME.breadcrumb}
      contextPill={`${roleByKey(role).label} · ${scope.officeCode} office`.toUpperCase()}
      nav={myNav(role)}
    >
      {/* PX-W6.6 (AC-1.6): this surface renders demo content and had no
          badge. Never hide a surface — badge it. */}
      <div className="mb-3">
        <ConceptBadge />
      </div>
      <div className="w-full">
        <header className="max-w-3xl">
          <h1 className="font-serif text-2xl font-semibold text-foreground">{MANAGER_HEADER.title}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{MANAGER_HEADER.lede}</p>
        </header>

        <p className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
          This landing is scoped to the{' '}
          <span className="font-medium text-foreground">{scope.officeName} office</span> —{' '}
          {scope.scopedCount} of {scope.activeCount} active engagements and the {scope.packageCount}{' '}
          work packages on them. People insight reads the whole group pool ({scope.poolSize} across{' '}
          {scope.officeCount} offices). The unscoped portfolio is{' '}
          <Link
            to={MANAGER_LABELS.unscopedRoute}
            className="text-accent-foreground underline-offset-4 hover:underline"
          >
            {MANAGER_LABELS.unscopedLink}
          </Link>
          .
        </p>

        <section className="mt-4 rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <SectionLabel>
              {MANAGER_LABELS.progressHeading} · {scope.officeCode} office
            </SectionLabel>
            <Link
              to={MANAGER_LABELS.unscopedRoute}
              className="text-xs text-accent-foreground underline-offset-4 hover:underline"
            >
              All {scope.activeCount} active engagements →
            </Link>
          </div>
          <div className="px-4 py-3">
            <table className="w-full text-left">
              <thead>
                <tr>
                  {Object.values(MANAGER_LABELS.columns).map((column, i) => (
                    <th
                      key={column}
                      scope="col"
                      className={`pb-2 pr-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground ${
                        i === Object.values(MANAGER_LABELS.columns).length - 1 ? 'text-right' : ''
                      }`}
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.project.id} className="v-row">
                    <td className="py-2.5 pr-3">
                      <Link
                        to={`/project/${row.project.id}`}
                        className="font-medium text-foreground underline-offset-4 hover:underline"
                      >
                        {row.project.client}
                      </Link>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {row.project.name.replace(`${row.project.client} — `, '')}
                      </div>
                    </td>
                    <td className="pr-3">
                      <StatusPill tone="neutral">{row.deliveryPhaseName}</StatusPill>
                    </td>
                    <td className="pr-3 text-sm text-ink-soft">{row.engagementLabel}</td>
                    <td className="pr-3">
                      <span className="font-mono text-[0.75rem] font-semibold tracking-[0.06em] text-foreground">
                        {row.gateName}
                      </span>
                    </td>
                    <td className="pr-3">
                      <StatusPill tone={row.gateIsClientFacing ? 'review' : 'neutral'}>
                        {row.gateStatus}
                      </StatusPill>
                    </td>
                    <td className="pr-3">
                      <StatusPill tone={healthTone[row.health]}>{row.health}</StatusPill>
                    </td>
                    <td className="v-figure pr-1 text-right text-sm tabular-nums text-ink-soft">
                      {row.staffed}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section
          aria-label={MANAGER_LABELS.peopleAriaLabel}
          className="mt-4 grid grid-cols-3 items-start gap-4"
        >
          <Panel heading={MANAGER_LABELS.overloaded}>
            <PeopleList people={overloaded} figureClass="text-blocked" />
            <p className="mt-2 text-xs text-muted-foreground">{overloadedNote}</p>
          </Panel>

          <Panel heading={MANAGER_LABELS.bench}>
            <PeopleList people={bench} figureClass="text-done" />
            <p className="mt-2 text-xs text-muted-foreground">{benchNote}</p>
          </Panel>

          <Panel heading={MANAGER_LABELS.gap}>
            {gap.workPackage && gap.project ? (
              <>
                <p className="text-sm font-medium text-foreground">{gap.workPackage.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {gap.project.client} · {gap.detail}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <StatusPill tone="review">{MANAGER_LABELS.needsPerson}</StatusPill>
                  {gap.bestPerson && <PersonChip person={gap.bestPerson} />}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{gap.bestNote}</p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Every work package has an owner.</p>
            )}
          </Panel>
        </section>

        <div className="mt-6 flex items-center justify-between gap-3">
          <SectionLabel>{MANAGER_LABELS.teamBoard}</SectionLabel>
          <span className="text-xs text-muted-foreground">{MANAGER_LABELS.teamBoardNote}</span>
        </div>
        <div className="mt-4">
          <KanbanBoard
            workPackages={board}
            columns={WORK_PACKAGE_STATUSES}
            columnLabels={COLUMN_LABELS}
            emptyLabel={BOARD_LABELS.emptyColumn}
            ariaLabel={BOARD_LABELS.boardAriaLabel}
            showProject
            showAssignee
          />
        </div>

        <DensityNote>{MANAGER_DENSITY_NOTE}</DensityNote>
      </div>
    </AppShell>
  )
}


function PeopleList({
  people,
  figureClass,
}: {
  people: readonly LoadedPerson[]
  figureClass: string
}) {
  return (
    <div className="flex flex-col gap-2">
      {people.map(({ person, figure }) => (
        <div key={person.id} className="flex items-center justify-between gap-2">
          <PersonChip person={person} />
          <span className={`v-figure text-sm font-semibold tabular-nums ${figureClass}`}>
            {figure}
          </span>
        </div>
      ))}
    </div>
  )
}

